import React from 'react'
import { ArrowDownTrayIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'

import { ArrowPathIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline'

import { BrandingDeployment, BrandingTemplate, downloadBrandingPackage } from '../services/api'
import { PSADT_LANGS } from '../utils/uxTemplateDraft'

/**
 * Settings -> Templates -> Branding.
 *
 * The DEVICE-WIDE appearance: a signed config.psd1 written to %ProgramData%\sap\psadt\config, which
 * every package on that device reads. Not a per-deployment setting — that is the User Experience
 * template's Appearance step, and a branded device overrides it.
 *
 * Each row says what state the branding is in and where it is deployed, because "ready" and "on
 * those machines" are different answers to the question an admin arrives with. Deploy is one press
 * with no screen behind it: only a signed branding may ship, and its audience was already chosen on
 * the editor's Assignment step.
 */

const STATE_COPY: Record<BrandingTemplate['state'], { label: string; tone: string; hint: string }> = {
  // 'building' covers both unsigned and mid-signing. An author cannot act on the difference: neither
  // can be deployed, and both resolve on their own.
  building: {
    label: 'Building',
    tone: 'text-amber-400 border-amber-400/40',
    hint: 'Being packaged and signed. It cannot be deployed until this finishes.',
  },
  ready: {
    label: 'Ready',
    tone: 'text-emerald-400 border-emerald-400/40',
    hint: 'Signed and deployable.',
  },
  failed: {
    label: 'Failed',
    tone: 'text-red-400 border-red-400/40',
    hint: 'The build did not finish. The reason is on the row.',
  },
}

/** What this branding IS, in one line: "Fluent · Acme Corp · French".
 *
 *  Distinct from the "Takes over" line below it, which says which settings it seizes from every
 *  deployment. This says what a device will actually look like — the question an admin comparing
 *  two brandings arrives with, and the one that otherwise needs the editor opened twice.
 *
 *  A setting this branding leaves alone is omitted rather than shown as a default, because the
 *  deployment's own choice applies there and naming one would be a claim about a value that was
 *  never written. */
const identityLine = (t: BrandingTemplate): string => {
  const lang = t.language_override
    ? PSADT_LANGS.find((l) => l.code === t.language_override)?.label ?? t.language_override
    : null
  return [t.dialog_style, t.company_name, lang].filter(Boolean).join(' · ')
}

const Pill: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${className ?? 'border-theme-border-primary text-theme-text-tertiary'}`}>
    {children}
  </span>
)

/** One row action, as the templates list draws them: an icon whose `title` is both the tooltip and
 *  the accessible name, so two words of chrome per row do not compete with the branding's name. */
const RowAction: React.FC<{
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  danger?: boolean
}> = ({ label, icon: Icon, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`rounded-md p-2 text-theme-text-tertiary transition-colors ${
      danger
        ? 'hover:bg-red-500/10 hover:text-red-500'
        : 'hover:bg-theme-tertiary hover:text-theme-text-primary'
    }`}
  >
    <Icon className="h-4 w-4" />
  </button>
)

/** The two artifacts a branding package can be handed over as.
 *
 *  The contents are listed FIRST and named for what they are, because they are what the question
 *  "why is this device not branded" is answered with: the .intunewin's payload is encrypted, so it
 *  proves what was uploaded rather than being something anyone can open and read. */
const ARTIFACTS = [
  { artifact: 'zip' as const, label: 'Contents', title:
      'Download config.psd1, the assets and the install scripts — what to compare against ' +
      '%ProgramData%\\sap\\psadt\\config on a device that is not showing this branding' },
  { artifact: 'intunewin' as const, label: '.intunewin', title:
      'Download the package as Intune received it. Its payload is encrypted, so this is for ' +
      're-uploading rather than for reading' },
]

/** How a deployment reads on the row it came from. */
const DEPLOY_STATE: Record<string, { label: string; tone: string }> = {
  publishing: { label: 'Publishing…', tone: 'text-amber-400' },
  published: { label: 'Deployed', tone: 'text-emerald-400' },
  failed: { label: 'Deployment failed', tone: 'text-red-400' },
  withdrawing: { label: 'Being removed', tone: 'text-blue-400' },
}

/** WHO a deployment reaches, in the audience's own terms.
 *
 *  Counting groups is not enough and was actively wrong: an All Devices assignment names no group,
 *  so a branding on every machine in the tenant read as "Deployed to 0 group(s)". */
const audienceOf = (d: BrandingDeployment): string => {
  const rows = (d.assignments ?? []) as any[]
  const kinds = new Set(
    rows.filter((r) => r?.target?.include !== false).map((r) => r?.target?.type),
  )
  if (kinds.has('allDevices')) return 'all devices'
  if (kinds.has('allUsers')) return 'all users'
  const n = d.group_ids?.length ?? 0
  return n === 1 ? '1 group' : `${n} groups`
}

/** WHO this branding would reach if it were deployed now.
 *
 *  The audience is authored on the editor's Assignment step and stored on the branding, so Deploy
 *  needs no screen of its own — and a branding that has none can only be published as an Intune
 *  application that reaches no device while reporting success. */
const hasAudience = (t: BrandingTemplate): boolean => (t.assignments?.length ?? 0) > 0

const NO_AUDIENCE =
  'This branding has no audience yet, so deploying it would create an Intune application that '
  + 'reaches no device. Edit it and choose who receives it on the Assignment step.'

/** Another branding already deployed, which this one would fight with on any shared device.
 *
 *  A device holds ONE Layer-3 configuration and reports ONE identity, and each branding's Intune
 *  application detects by its own. So two live brandings reaching the same machine each see the
 *  other's marker as "not installed" and reinstall over it, indefinitely. Install order decides
 *  which is showing at any moment, which is Intune's to schedule and nobody's to predict.
 *
 *  Warned rather than refused: overlapping audiences cannot be resolved here without expanding
 *  every group, and a tenant deliberately staging two brandings has a legitimate reason to. */
const collidesWith = (
  t: BrandingTemplate,
  deployments: BrandingDeployment[],
): BrandingDeployment[] =>
  deployments.filter((d) => d.status === 'published' && d.identity !== t.identity)

/** Why Deploy cannot be pressed, or null.
 *
 *  A branding is ONE application per identity per tenant — the database says so — so deploying an
 *  unchanged branding a second time is refused by the server. Saying that here beats letting the
 *  press fail, and disabling while one is in flight stops a double-press racing itself. */
const deployBlocked = (
  t: BrandingTemplate,
  live: BrandingDeployment[],
  busyId?: string | null,
): string | null => {
  if (busyId === t.id) return 'Deploying…'
  if (live.some((d) => d.status === 'publishing')) {
    return 'This branding is being published to Intune. Refresh to see when it lands.'
  }
  if (live.some((d) => d.status === 'published')) {
    return 'This exact branding is already deployed. Edit it to deploy a new version, or use '
      + 'Undeploy to take it out of Intune.'
  }
  // NO AUDIENCE. The server refuses this too, and refusing it here is the difference between a
  // disabled button that says what is missing and a press that comes back as a red error — the
  // audience lives on the branding's own Assignment step, which is where the reader has to go.
  //
  // Only once the package itself is deployable: a branding still building is waiting on the build
  // first, and naming the audience would send its author to fix something that is not the holdup.
  if (t.deployable && !hasAudience(t)) return NO_AUDIENCE
  return null
}

const BrandingPanel: React.FC<{
  templates: BrandingTemplate[]
  deployments: BrandingDeployment[]
  loading: boolean
  error: string | null
  onCreate?: () => void
  /** Both are withheld from a preshipped branding: the API answers 403 to an edit or a delete on
   *  one, so offering either would teach an admin that the portal lies to them. */
  onEdit?: (t: BrandingTemplate) => void
  onDelete?: (t: BrandingTemplate) => void
  /** Offered only for a branding that is signed — the API refuses any other. */
  onDeploy?: (t: BrandingTemplate) => void
  /** Build again from what is saved. Offered on FAILED rows only: a build that has not finished is
   *  not stuck, and offering retry beside it invites an author to restart something already
   *  running. A Ready branding has nothing to retry. */
  onRebuild?: (t: BrandingTemplate) => void
  /** UNDEPLOY: uninstall this branding from its audience, then take the application out of Intune.
   *  Offered on a branding that HAS a deployment, because it acts on that deployment. */
  onUndeploy?: (t: BrandingTemplate, d: BrandingDeployment) => void
  /** The branding an action is in flight for, so the row can say so and refuse a second press. */
  busyId?: string | null
}> = ({ templates, deployments, loading, error, onCreate, onEdit, onDelete, onDeploy, onRebuild,
        onUndeploy, busyId }) => {
  /** Deployments for a branding, matched by IDENTITY rather than by template id.
   *
   *  A deployment carries a frozen copy of what it shipped, so editing the template moves it on
   *  while the deployment keeps describing what devices actually received. Matching on the template
   *  would claim the deployment shows the current wording, which it does not. */
  const deployedFor = (t: BrandingTemplate) =>
    deployments.filter((d) => d.identity && d.identity === t.identity)

  // Keyed by branding AND artifact: two downloads of the same branding are separate actions, and
  // one key would disable both while either ran.
  const [downloading, setDownloading] = React.useState<string | null>(null)
  const [downloadError, setDownloadError] = React.useState<string | null>(null)

  const download = async (t: BrandingTemplate, artifact: 'zip' | 'intunewin') => {
    setDownloading(`${t.id}:${artifact}`)
    setDownloadError(null)
    try {
      const blob = await downloadBrandingPackage(t.id, artifact)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      // The server names the file after the branding AND its identity; this is only the fallback
      // for a response that arrives without one.
      link.download = `${t.name}.${artifact === 'zip' ? 'zip' : 'intunewin'}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      // The response is a blob, so its body carries no readable detail. Said plainly rather than
      // left as a button that silently re-enables.
      setDownloadError(`Could not build the ${artifact === 'zip' ? 'contents' : '.intunewin'} for ${t.name}.`)
    } finally {
      setDownloading(null)
    }
  }

  return (
    <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold text-theme-text-primary mb-1">Branding</h3>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary"
          >
            New branding
          </button>
        )}
      </div>
      <p className="text-xs text-theme-text-tertiary mb-4">
        Applies to the whole device, not to one application. A branding package writes a signed
        configuration to the machine, and every application deployed there uses it — so a device
        shows one logo and one company name whatever it installs.
      </p>

      {loading && <p className="text-sm text-theme-text-tertiary">Loading…</p>}
      {error && <p className="text-sm text-red-400">{error}</p>}
      {downloadError && <p className="mb-3 text-sm text-red-400">{downloadError}</p>}

      {!loading && !error && templates.length === 0 && (
        <div className="rounded-lg border border-dashed border-theme-border-primary px-4 py-6">
          <p className="text-sm text-theme-text-secondary">No branding yet.</p>
          <p className="mt-1 text-xs text-theme-text-tertiary">
            Until one is deployed, dialogs show each application&apos;s own icon and PSADT&apos;s
            default wording. That is a working state, not a broken one.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {templates.map((t) => {
          const state = STATE_COPY[t.state] ?? STATE_COPY.building
          const live = deployedFor(t)
          const collisions = collidesWith(t, deployments)
          return (
            <div
              key={t.id}
              className="rounded-lg border border-theme-border-primary bg-theme-primary px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-theme-text-primary">{t.name}</span>
                    <Pill className={state.tone}>{state.label}</Pill>
                    {t.read_only && <Pill>Read-only</Pill>}
                  </div>

                  {t.description && (
                    <p className="mt-1 text-xs text-theme-text-tertiary">{t.description}</p>
                  )}

                  {identityLine(t) && (
                    <p className="mt-1 text-xs text-theme-text-secondary">{identityLine(t)}</p>
                  )}

                  {/* What this branding TAKES OVER on a device that holds it. Named because the
                      on-device configuration wins over every deployment, so an author whose template
                      setting had no effect needs to be able to see why without reading a .psd1. */}
                  {t.settings?.length > 0 && (
                    <p className="mt-1.5 text-xs text-theme-text-tertiary">
                      Takes over: <span className="text-theme-text-secondary">{t.settings.join(', ')}</span>
                    </p>
                  )}

                  {t.state === 'failed' && t.error && (
                    <p className="mt-1.5 text-xs text-red-400">{t.error}</p>
                  )}
                  {/* Named next to the reason it failed, because that is where an author is looking
                      when they decide whether it is worth trying again. A build can fail for
                      reasons that have nothing to do with the branding — an unreachable signer, a
                      restarted worker — and those clear on their own. */}
                  {t.state === 'failed' && !t.read_only && onRebuild && (
                    <p className="mt-1 text-xs text-theme-text-tertiary">
                      Nothing about this branding changes when it is built again.
                    </p>
                  )}
                  {t.state !== 'failed' && (
                    <p className="mt-1.5 text-xs text-theme-text-tertiary">{state.hint}</p>
                  )}

                  {/* THE MISSING STEP, ON THE ROW rather than only inside the disabled Deploy's
                      tooltip. An audience is chosen in the editor, so a reader looking at a branding
                      that says Ready and will not deploy has nothing here to tell them where to go
                      — and pressing Deploy to find out returns the same sentence as a red error. */}
                  {live.length === 0 && !hasAudience(t) && (
                    <p className="mt-2 text-xs text-theme-text-tertiary">
                      No audience yet.{' '}
                      {!t.read_only && onEdit ? (
                        <button
                          type="button"
                          onClick={() => onEdit(t)}
                          className="font-medium text-theme-accent-blue hover:underline"
                        >
                          Choose who receives it
                        </button>
                      ) : (
                        <span>Choose who receives it</span>
                      )}{' '}
                      on the Assignment step before deploying.
                    </p>
                  )}

                  {/* Where it actually is. "Ready" and "deployed" are different states and an admin
                      asking about a device cares only about the second. */}
                  {/* Named BEFORE the press, because after it the estate is already in the state
                      this warns about and Intune decides which branding wins. */}
                  {live.length === 0 && collisions.length > 0 && t.deployable && (
                    <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-500">
                      <strong>{collisions.map((c) => c.name).join(', ')}</strong>{' '}
                      {collisions.length === 1 ? 'is' : 'are'} already deployed. A device reached by
                      both holds only one branding and reports one identity, so each application
                      detects the other as missing and reinstalls over it. Undeploy the other one
                      first unless their audiences genuinely do not overlap.
                    </p>
                  )}

                  {/* THE DEPLOYMENT'S OWN STATE, not just a group count. A branding can be
                      publishing, published, failed or withdrawing, and an operator pressing Deploy
                      needs to see which — "Deployed to 0 group(s)" said nothing about any of it,
                      and read as "not deployed" for an All Devices assignment, which names no
                      group and reaches every machine in the tenant. */}
                  <p className="mt-2 text-xs">
                    {live.length === 0 ? (
                      <span className="text-theme-text-tertiary">Not deployed.</span>
                    ) : (
                      live.map((d) => {
                        const s = DEPLOY_STATE[d.status] ?? DEPLOY_STATE.publishing
                        return (
                          <span key={d.id} className={`mr-3 ${s.tone}`}>
                            {s.label} — {audienceOf(d)}
                          </span>
                        )
                      })
                    )}
                  </p>

                  {/* The package itself. Offered on the same gate as Deploy: both need a signed
                      branding, and there is nothing to hand over before one exists. */}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {ARTIFACTS.map((a) => (
                      <button
                        key={a.artifact}
                        type="button"
                        disabled={!t.deployable || downloading === `${t.id}:${a.artifact}`}
                        onClick={() => download(t, a.artifact)}
                        title={
                          t.deployable
                            ? a.title
                            : 'Available once this branding has finished building and been signed.'
                        }
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-theme-accent-blue hover:underline disabled:cursor-not-allowed disabled:text-theme-text-tertiary disabled:no-underline disabled:opacity-60"
                      >
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                        {downloading === `${t.id}:${a.artifact}` ? 'Preparing…' : a.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {/* SHOWN EVEN WHEN IT CANNOT BE USED, disabled and with the reason on it. Hiding
                      it left an admin looking at a branding with no action at all and no way to tell
                      whether one was coming — which reads as broken rather than as pending. */}
                  {onDeploy && (
                    <button
                      type="button"
                      onClick={() => t.deployable && !deployBlocked(t, live, busyId) && onDeploy(t)}
                      disabled={!t.deployable || !!deployBlocked(t, live, busyId)}
                      title={
                        deployBlocked(t, live, busyId)
                          ? deployBlocked(t, live, busyId)!
                          : t.deployable
                          ? 'Send this branding to the audience on its Assignment step'
                          : t.state === 'failed'
                            ? 'This branding failed to build, so there is nothing to deploy. The reason is on the row.'
                            : 'Available once this branding has finished building and been signed.'
                      }
                      className={`rounded-lg border px-2.5 py-1 text-xs ${
                        t.deployable && !deployBlocked(t, live, busyId)
                          ? 'border-theme-border-primary text-theme-text-primary hover:bg-theme-tertiary'
                          : 'cursor-not-allowed border-theme-border-primary/50 text-theme-text-tertiary opacity-60'
                      }`}
                    >
                      {busyId === t.id ? 'Deploying…' : 'Deploy'}
                    </button>
                  )}
                  {t.state === 'failed' && !t.read_only && onRebuild && (
                    <RowAction label="Build again" icon={ArrowPathIcon} onClick={() => onRebuild(t)} />
                  )}
                  {!t.read_only && onEdit && (
                    <RowAction label="Edit" icon={PencilIcon} onClick={() => onEdit(t)} />
                  )}
                  {/* The opposite of Deploy, and named as such. It undoes the deployment on both
                      sides: off the devices, then out of Intune. */}
                  {onUndeploy && live.length > 0 && (
                    <RowAction
                      label="Undeploy"
                      icon={ArrowUturnLeftIcon}
                      onClick={() => onUndeploy(t, live[0])}
                      danger
                    />
                  )}
                  {!t.read_only && onDelete && (
                    <RowAction label="Delete" icon={TrashIcon} onClick={() => onDelete(t)} danger />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Deployments that no longer match a current branding are NOT listed here. They are still on
          devices and still matter, which is why they have their own tab — a list of things you can
          edit is the wrong place for a record of what has already shipped and cannot be. */}
    </div>
  )
}

export default BrandingPanel
